import { useState, useEffect, useCallback } from 'react';
import { lookupsApi, equipmentApi } from '../services/api';

/**
 * Custom hook to fetch and cache lookup data from Data Management
 * @param {string} category - The lookup category (e.g., 'asset_category', 'defect_severity')
 * @returns {object} { data, loading, error, refetch }
 */
export const useLookup = (category) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!category) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await lookupsApi.getByCategory(category);
      setData(response.data);
    } catch (err) {
      console.error(`Failed to fetch lookups for ${category}:`, err);
      setError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Custom hook to fetch equipment makes
 * @returns {object} { data, loading, error, refetch }
 */
export const useEquipmentMakes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await equipmentApi.getMakes();
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch equipment makes:', err);
      setError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Custom hook to fetch equipment models, optionally filtered by make
 * @param {string|null} makeId - Optional make ID to filter models
 * @returns {object} { data, loading, error, refetch }
 */
export const useEquipmentModels = (makeId = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await equipmentApi.getModels(makeId);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch equipment models:', err);
      setError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [makeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Fetch multiple lookup categories at once
 * @param {string[]} categories - Array of category names
 * @returns {object} { data: {category: items[]}, loading, error }
 */
export const useMultipleLookups = (categories) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!categories || categories.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const results = await Promise.all(
        categories.map(cat => 
          lookupsApi.getByCategory(cat).then(res => ({ category: cat, items: res.data }))
        )
      );
      
      const dataMap = {};
      results.forEach(({ category, items }) => {
        dataMap[category] = items;
      });
      
      setData(dataMap);
    } catch (err) {
      console.error('Failed to fetch lookups:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [categories?.join(',')]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

export default useLookup;
